/**
 * Editor / preview-only variable nesting: depth maps and partial substitution
 * for canvas text. Production renders omit preview options (full resolve).
 */

import { applyVariables, scanForVars } from './variables';

/** Fresh regex per scan — shared `/g` regex + recursion corrupts lastIndex. */
function createVariableTokenRe(): RegExp {
  return /\{\{([^}]+)\}\}/g;
}

export type TextLayerPreviewMode = 'production' | 'tokens' | 'depth';

/** When mode is production or omitted, canvas uses full nested applyVariables. */
export interface TextLayerPreviewOptions {
  mode: TextLayerPreviewMode;
  /** BFS depths from template roots (1 = root token in layer text). */
  keyDepths: Record<string, number>;
  /** max(depths) or 1 when empty */
  maxDepth: number;
  /** For mode === 'depth': 0 shows template tokens; maxDepth fully resolves. */
  depth: number;
  hasCycle?: boolean;
}

export function collectVariableRootsFromTexts(texts: Array<string | null | undefined>): Set<string> {
  const roots = new Set<string>();
  for (const t of texts) {
    for (const v of scanForVars(String(t ?? ''))) {
      const k = v.trim();
      if (k) roots.add(k);
    }
  }
  return roots;
}

/**
 * Shortest-path depths from root keys through values (edge k -> inner if inner appears in vars[k]).
 */
export function buildVariableKeyDepthMap(
  rootKeys: Iterable<string>,
  vars: Record<string, string | number | null | undefined>,
): { depths: Record<string, number>; hasCycle: boolean; maxDepth: number } {
  const depths: Record<string, number> = {};
  const queue: string[] = [];

  for (const raw of rootKeys) {
    const k = String(raw || '').trim();
    if (!k) continue;
    depths[k] = 1;
    queue.push(k);
  }

  let hasCycle = false;

  while (queue.length) {
    const key = queue.shift()!;
    const d = depths[key];
    if (d == null) continue;

    const str = String(vars[key] ?? '');
    for (const inner of scanForVars(str)) {
      const ik = inner.trim();
      if (!ik) continue;
      if (ik === key) {
        hasCycle = true;
        continue;
      }
      const next = d + 1;
      if (depths[ik] == null || next < depths[ik]) {
        depths[ik] = next;
        queue.push(ik);
      }
    }
  }

  const vals = Object.values(depths);
  const maxDepth = vals.length ? Math.max(...vals) : 1;
  return { depths, hasCycle, maxDepth: Math.max(1, maxDepth) };
}

/**
 * Replace {{inner}} in `value` when depth(inner) <= D with recursively formatted values;
 * leave tokens whose depth > D as literal {{inner}}.
 */
export function formatVariableValueAtDepth(
  value: string,
  depthLimit: number,
  keyDepths: Record<string, number>,
  vars: Record<string, string | number | null | undefined>,
): string {
  const source = String(value ?? '');
  if (!source.length) return '';

  let out = '';
  let last = 0;
  const re = createVariableTokenRe();
  let m: RegExpExecArray | null;
  while ((m = re.exec(source))) {
    out += source.slice(last, m.index);
    const innerKey = m[1].trim();
    const dep = keyDepths[innerKey] ?? 999999;
    if (dep > depthLimit) {
      out += m[0];
    } else {
      const sub = vars[innerKey];
      const chunk = sub == null ? '' : String(sub);
      out += formatVariableValueAtDepth(chunk, depthLimit, keyDepths, vars);
    }
    last = m.index + m[0].length;
  }
  out += source.slice(last);
  return out;
}

/**
 * Expand a full template string for FlowText / sidebar previews using the same
 * token rules as canvas segments (top-level {{k}} spans call resolveTextSegmentForPreview).
 */
export function resolveTemplateTextWithPreview(
  rawText: string,
  vars: Record<string, string | number | null | undefined>,
  preview: TextLayerPreviewOptions | undefined,
  useMissingPlaceholder = false,
): string {
  const onMissing = (key: string) => (useMissingPlaceholder ? `(MUST ADD {{${key}}} VALUE)` : '');
  if (!preview || preview.mode === 'production') {
    return applyVariables(String(rawText || ''), vars, { onMissing });
  }
  if (preview.mode === 'tokens') {
    return String(rawText || '');
  }
  const source = String(rawText || '');
  let out = '';
  let last = 0;
  const re = createVariableTokenRe();
  let m: RegExpExecArray | null;
  while ((m = re.exec(source))) {
    out += source.slice(last, m.index);
    const key = m[1].trim();
    if (!Object.prototype.hasOwnProperty.call(vars, key)) {
      out += onMissing(key);
    } else {
      const rawVal = vars[key];
      out += resolveTextSegmentForPreview(key, rawVal, vars, preview);
    }
    last = m.index + m[0].length;
  }
  out += source.slice(last);
  return out;
}

export function buildTextLayerPreviewFromWindowConfig(
  cfg: { mode?: string; depth?: number } | null | undefined,
  layerOrBlockText: string,
  vars: Record<string, string | number | null | undefined>,
): TextLayerPreviewOptions | undefined {
  if (!cfg || cfg.mode === 'production' || cfg.mode === undefined) {
    return undefined;
  }
  const roots = collectVariableRootsFromTexts([layerOrBlockText]);
  const { depths, hasCycle, maxDepth } = buildVariableKeyDepthMap(roots, vars);
  if (cfg.mode === 'tokens') {
    return { mode: 'tokens', keyDepths: depths, maxDepth, depth: 0, hasCycle };
  }
  if (cfg.mode === 'depth') {
    const d = Math.max(0, Math.min(Number(cfg.depth) || 0, maxDepth));
    return { mode: 'depth', keyDepths: depths, maxDepth, depth: d, hasCycle };
  }
  return undefined;
}

export function resolveTextSegmentForPreview(
  variableKey: string,
  rawValue: unknown,
  vars: Record<string, string | number | null | undefined>,
  preview: TextLayerPreviewOptions | undefined,
): string {
  const key = variableKey.trim();
  const rawStr = rawValue == null ? '' : String(rawValue);

  if (!preview || preview.mode === 'production') {
    return applyVariables(rawStr, vars);
  }

  if (preview.mode === 'tokens') {
    return `{{${key}}}`;
  }

  if (preview.mode === 'depth') {
    const maxD = Math.max(1, preview.maxDepth);
    const D = Math.max(0, Math.min(Number(preview.depth) || 0, maxD));
    if (D >= maxD) {
      return applyVariables(rawStr, vars);
    }
    if (D <= 0) {
      return `{{${key}}}`;
    }
    return formatVariableValueAtDepth(rawStr, D, preview.keyDepths, vars);
  }

  return applyVariables(rawStr, vars);
}
